// File: src/screens/profile/tabs/ProfileTab.js
import React, { useEffect, useState } from "react";
import api from "../../../api/client";
import { fetchMyAddresses } from "../../../api/userAddresses";
import logger from "../../../utils/logger.js";

function toAbsoluteUrl(raw) {
  const u = String(raw || "").trim();
  if (!u) return "";

  if (/^(https?:\/\/|blob:|data:)/i.test(u)) return u;

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const clean = u.replace(/^\/+/, "");
  return origin ? `${origin}/${clean}` : `/${clean}`;
}

export default function ProfileTab({
  user,
  form,
  setForm,
  isAr,
  labels,
  onOpenAddresses,
}) {
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview("");
      return;
    }

    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  useEffect(() => {
    let cancelled = false;

    async function loadDefaultAddress() {
      try {
        const addresses = await fetchMyAddresses();
        if (cancelled) return;
        const nextDefaultAddress =
          (Array.isArray(addresses) ? addresses.find((address) => Boolean(address.is_default)) : null) ||
          (Array.isArray(addresses) ? addresses[0] : null) ||
          null;
        setDefaultAddress(nextDefaultAddress);
      } catch (error) {
        if (!cancelled) {
          logger.warn("Failed to load default address for profile tab", error);
          setDefaultAddress(null);
        }
      }
    }

    loadDefaultAddress();

    return () => {
      cancelled = true;
    };
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function handleAvatarChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setAvatarFile(null);
      setAvatarError("");
      return;
    }

    // Validate file type
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      setAvatarError("Only JPG, PNG, WebP, and GIF images are allowed");
      setAvatarFile(null);
      return;
    }

    if (file.size > maxSize) {
      setAvatarError("Image must be smaller than 5MB");
      setAvatarFile(null);
      return;
    }

    setAvatarError("");
    setAvatarFile(file);
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);

    try {
      let payload;

      if (avatarFile) {
        payload = new FormData();
        payload.append("name", form.name || "");
        payload.append("phone", form.phone || "");
        if (form.password) payload.append("password", form.password);
        if (form.avatar_url) payload.append("avatar_url", form.avatar_url);
        payload.append("avatar", avatarFile);
      } else {
        payload = {
          name: form.name || "",
          avatar_url: form.avatar_url || "",
          phone: form.phone || "",
          password: form.password || "",
        };
      }

      const res = await api.apiPost("profile/update", payload);

      if (res.user) {
        setForm((f) => ({
          ...f,
          name: res.user.name || "",
          avatar_url: res.user.avatar_url || "",
          phone: res.user.phone || "",
          password: "",
        }));

        setAvatarFile(null);
        setAvatarPreview("");

        alert(labels.profileUpdated || (isAr ? "تم الحفظ" : "Saved"));
      } else {
        alert(res.message || labels.profileUpdateFailed || "Error");
      }
    } catch (e) {
      logger.error("save profile error", e);
      alert(labels.profileUpdateFailed || (isAr ? "خطأ بالحفظ" : "Error while saving"));
    } finally {
      setSaving(false);
    }
  }

  const avatarSrc = toAbsoluteUrl(
    avatarPreview || form.avatar_url || (user && user.avatar_url) || ""
  );

  const inputClassName = "form-control rounded-3";
  const sectionTitleClassName = "fw-semibold mb-3";
  const profileAddressTitle = [form.street_address, form.neighborhood, form.city]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
  const profileAddressDetails = [form.address, form.country_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" • ");
  const hasProfileAddress = Boolean(profileAddressTitle || profileAddressDetails);
  const primaryAddressTitle = hasProfileAddress
    ? profileAddressTitle || form.address || form.country_name
    : [defaultAddress?.street, defaultAddress?.neighborhood, defaultAddress?.city]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(", ");
  const primaryAddressDetails = hasProfileAddress
    ? profileAddressDetails
    : String(defaultAddress?.address_text || "").trim();
  const primaryAddressBadge = hasProfileAddress
    ? isAr
      ? "عنوان الحساب"
      : "Account address"
    : isAr
      ? "العنوان الافتراضي"
      : "Default address";

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header fw-semibold">
        {labels.personal || (isAr ? "البيانات الشخصية" : "Personal Information")}
      </div>

      <div className="card-body">
        <form onSubmit={saveProfile}>
          <div className="row g-4">
            <div className="col-12 col-xl-4">
              <div className="border rounded-4 p-4 h-100 bg-light-subtle">
                <div className={sectionTitleClassName}>
                  {labels.avatar || (isAr ? "الصورة الشخصية" : "Avatar")}
                </div>

                <div className="d-flex flex-column align-items-center text-center">
                  <div className="mb-3">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt="avatar"
                        style={{
                          width: 104,
                          height: 104,
                          objectFit: "cover",
                          borderRadius: "50%",
                          border: "3px solid rgba(0,0,0,0.08)",
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-white border"
                        style={{ width: 104, height: 104, fontSize: 36 }}
                      >
                        {String(form.name || user?.name || "U").trim().charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="w-100">
                    <input
                      type="file"
                      accept="image/*"
                      className={`${inputClassName} mb-2`}
                      onChange={handleAvatarChange}
                    />

                    {avatarError && <div className="alert alert-danger py-2 mb-2">{avatarError}</div>}

                    <small className="text-muted d-block mb-2">
                      {labels.avatar_hint ||
                        (isAr
                          ? "يمكنك رفع صورة أو وضع رابط صورة مباشرة"
                          : "You can upload an image or paste an avatar URL")}
                    </small>

                    <input
                      type="text"
                      name="avatar_url"
                      className={inputClassName}
                      value={form.avatar_url}
                      onChange={handleChange}
                      placeholder="https://example.com/avatar.png"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-8">
              <div className="border rounded-4 p-4 h-100">
                <div className={sectionTitleClassName}>
                  {labels.personal || (isAr ? "البيانات الشخصية" : "Personal Information")}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="profile-name" className="form-label">{labels.name || (isAr ? "الاسم" : "Name")}</label>
                    <input
                      id="profile-name"
                      type="text"
                      name="name"
                      className={inputClassName}
                      value={form.name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-email" className="form-label">{labels.email || (isAr ? "البريد الإلكتروني" : "Email")}</label>
                    <input
                      id="profile-email"
                      type="email"
                      className={inputClassName}
                      value={user?.email || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-phone" className="form-label">
                      {labels.phone || (isAr ? "الهاتف" : "Phone")}
                      {!form.phone && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      id="profile-phone"
                      type="text"
                      name="phone"
                      className={inputClassName}
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={isAr ? "أضف رقم هاتفك" : "Add your phone number"}
                      style={!form.phone ? { borderColor: "#dc3545", backgroundColor: "#fff5f5" } : {}}
                      aria-describedby={!form.phone ? "profile-phone-hint" : undefined}
                    />
                    {!form.phone && (
                      <small id="profile-phone-hint" className="text-danger d-block mt-2">
                        {isAr ? "الرجاء إضافة رقم هاتفك" : "Please add your phone number"}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="profile-country" className="form-label">{labels.country || (isAr ? "الدولة" : "Country")}</label>
                    <input
                      id="profile-country"
                      type="text"
                      className={inputClassName}
                      value={user?.country_name || ""}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded-4 p-4">
                <div className={sectionTitleClassName}>
                  {isAr ? "العنوان الأساسي" : "Primary Address"}
                </div>

                {hasProfileAddress || defaultAddress ? (
                  <div className="border rounded-4 bg-light-subtle p-3 p-md-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                      <div>
                        <div className="fw-semibold mb-2">
                          {primaryAddressTitle || (isAr ? "عنوان المستخدم" : "User address")}
                        </div>
                        {primaryAddressDetails ? (
                          <div className="text-muted small">{primaryAddressDetails}</div>
                        ) : null}
                      </div>
                      <span className="badge bg-success">
                        {primaryAddressBadge}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small">
                    {isAr
                      ? "لا يوجد عنوان محفوظ بعد. أضف عنوانك من تبويب العناوين."
                      : "No saved address yet. Add one from the Addresses tab."}
                  </div>
                )}

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-dark rounded-pill px-4"
                    onClick={() => onOpenAddresses?.()}
                  >
                    {isAr ? "إدارة العناوين" : "Manage addresses"}
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="border rounded-4 p-4">
                <div className={sectionTitleClassName}>
                  {isAr ? "الأمان" : "Security"}
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-md-8">
                    <label className="form-label">
                      {labels.password || (isAr ? "كلمة مرور جديدة" : "New Password")}
                    </label>
                    <input
                      type="password"
                      name="password"
                      className={inputClassName}
                      value={form.password}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-4">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 rounded-pill py-2 fw-semibold"
                      disabled={saving}
                    >
                      {saving
                        ? labels.saving || (isAr ? "جارِ الحفظ..." : "Saving...")
                        : labels.save || (isAr ? "حفظ التغييرات" : "Save Changes")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
